/**
 * MediaResourceV2 — Media Resources list shell + grid, ported from
 * the prototype's `.screen-media` (cms-redesign-mockup.html ~7498+,
 * grid styles 3345–3415).
 *
 * Layout:
 *   • PageHeader (eyebrow Content Assets, title 'Media Resources N
 *     total', sub copy, coral '+ Add Resource').
 *   • Toolbar with a small search input over name + description.
 *   • `.mr-grid` — repeat(auto-fill, minmax(160px, 1fr)) of cards.
 *     Each card:
 *       – Square thumbnail (`resource_url`), cover-fit on cream.
 *       – Hover-revealed action cluster (View / Edit / Delete) over
 *         a darkening bottom-aligned gradient. Buttons are 30px
 *         white-frosted circles.
 *       – Meta strip below: name + 2-line description clamp.
 *
 * Reuses the v1 `fetchAllMediaResources` action and the existing
 * MediaResourceModal + MediaResourceUpdateForm components for
 * View / Edit (they're the same modals — porting their visuals is
 * out of scope here and they already match MUI conventions; the v2
 * page is just the list shell + grid).
 */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import {
  deleteMediaResource,
  fetchAllMediaResources,
} from '../../../../actions/mediaResourceActions';
import MediaResourceModal from '../../../MediaResource/MediaResourceModal';
import MediaResourceUpdateForm from '../../../MediaResource/MediaResourceUpdateForm';
import { PageHeader, Button, cn } from '../../../redesign';

/* ─── Icons ────────────────────────────────────────────────────────── */

const PlusSign = (
  <span style={{ fontSize: 16, lineHeight: 1 }} aria-hidden>+</span>
);

const EyeSvg = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EditSvg = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z" />
  </svg>
);

const TrashSvg = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
  </svg>
);

/* ─── Action button (overlay on hover) ─────────────────────────────── */

function MrButton({ children, onClick, danger, label }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick?.();
      }}
      aria-label={label}
      className="grid place-items-center cursor-pointer"
      style={{
        width: 30,
        height: 30,
        background: '#FFFFFFEE',
        border: '1px solid #FFFFFF',
        borderRadius: '50%',
        color: '#2C3E50',
        backdropFilter: 'blur(4px)',
        transition: 'all 140ms ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = '#FFFFFF';
        e.currentTarget.style.transform = 'scale(1.06)';
        e.currentTarget.style.color = danger ? '#C4715B' : '#5C7E72';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = '#FFFFFFEE';
        e.currentTarget.style.transform = 'none';
        e.currentTarget.style.color = '#2C3E50';
      }}
    >
      {children}
    </button>
  );
}

/* ─── Card ─────────────────────────────────────────────────────────── */

function MediaResourceCard({ resource, onView, onEdit, onDelete }) {
  return (
    <div
      className={cn(
        'group bg-surface border border-sand overflow-hidden cursor-pointer',
        'transition-all duration-200 hover:border-teal-green',
        'relative',
      )}
      style={{ borderRadius: 12 }}
      onClick={onView}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 10px 24px #2C3E5012';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = '';
        e.currentTarget.style.boxShadow = '';
      }}
    >
      <div
        className="bg-cream border-b border-sand"
        style={{
          aspectRatio: '1 / 1',
          backgroundImage: resource.resource_url
            ? `url('${resource.resource_url}')`
            : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          position: 'relative',
        }}
      >
        {/* Overlay cluster — hover-revealed gradient + 30px circular
         *  action buttons. */}
        <div
          className="opacity-0 group-hover:opacity-100 transition-opacity duration-150"
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(180deg, transparent 40%, #00000088 100%)',
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
            gap: 6,
            padding: 10,
          }}
        >
          <MrButton onClick={onView} label="View">{EyeSvg}</MrButton>
          <MrButton onClick={onEdit} label="Edit">{EditSvg}</MrButton>
          <MrButton onClick={onDelete} danger label="Delete">{TrashSvg}</MrButton>
        </div>
      </div>

      <div style={{ padding: '8px 10px 10px', minWidth: 0 }}>
        <div
          className="font-semibold text-navy truncate"
          style={{ fontSize: 12.5 }}
          title={resource.name || resource.file_name || ''}
        >
          {resource.name || resource.file_name || 'Untitled'}
        </div>
        {resource.description && (
          <div
            className="text-text-muted"
            style={{
              fontSize: 11,
              lineHeight: 1.3,
              marginTop: 2,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {resource.description}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Main ─────────────────────────────────────────────────────────── */

export default function MediaResourceV2() {
  const dispatch = useDispatch();
  const items = useSelector((s) => s.mediaResources.mediaResources);
  const loading = useSelector((s) => s.mediaResources.loading);

  const [openView, setOpenView] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);

  useEffect(() => {
    dispatch(fetchAllMediaResources());
  }, [dispatch]);

  // Search lives in the global ⌘K palette; no in-page search input.
  const visible = Array.isArray(items) ? items : [];

  const totalLabel = Array.isArray(items)
    ? `${items.length.toLocaleString()} total`
    : undefined;

  const handleView = (resource) => {
    setSelectedMedia(resource);
    setOpenView(true);
  };
  const handleEdit = (resource) => {
    setSelectedMedia(resource);
    setOpenEdit(true);
  };
  const handleDelete = (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'You will not be able to recover this media resource!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'No, cancel',
    }).then((result) => {
      if (result.value) {
        dispatch(deleteMediaResource(id));
      }
    });
  };

  return (
    <div className="bg-bg min-h-full">
      <PageHeader
        eyebrow="Content Assets"
        title="Media Resources"
        total={totalLabel}
        actions={
          <a href="/media-resources/create" className="no-underline">
            <Button variant="coral" leadingIcon={PlusSign}>
              Add Resource
            </Button>
          </a>
        }
      />

      {loading && !Array.isArray(items) ? (
        <div
          className="flex items-center justify-center text-text-muted"
          style={{ height: 256 }}
        >
          Loading media resources…
        </div>
      ) : visible.length === 0 ? (
        <div
          className="bg-surface border border-sand text-text-muted italic text-center"
          style={{
            borderRadius: 12,
            padding: '40px 16px',
            fontSize: 13,
            margin: '0 28px 32px',
          }}
        >
          No media resources yet — upload one to get started.
        </div>
      ) : (
        <div style={{ padding: '0 28px 32px' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
              gridAutoRows: 'max-content',
              gap: 14,
            }}
          >
            {visible.map((r) => (
              <MediaResourceCard
                key={r.id}
                resource={r}
                onView={() => handleView(r)}
                onEdit={() => handleEdit(r)}
                onDelete={() => handleDelete(r.id)}
              />
            ))}
          </div>
        </div>
      )}

      <MediaResourceModal
        open={openView}
        onClose={() => {
          setOpenView(false);
          setSelectedMedia(null);
        }}
        mediaResource={selectedMedia}
      />
      <MediaResourceUpdateForm
        open={openEdit}
        onClose={() => {
          setOpenEdit(false);
          setSelectedMedia(null);
        }}
        mediaResource={selectedMedia}
        setMediaResource={setSelectedMedia}
      />
    </div>
  );
}
